/**
 * HTTP request (fetch) helpers.
 */

import { HTTP_EVENT } from '../../constants';
import { RequestError } from '../errors';
import { getCookie } from './cookie';
import { triggerGlobalCustomEvent } from './dom';
import { isNil, isArray, isPlainObject } from './js';

// Mime types
export const JSON_MIME_TYPE = 'application/json';
export const JSON_MIME_TYPE_REGEX = new RegExp(JSON_MIME_TYPE);

/**
 * Wrapped fetch, used for exported GET and POST helpers.
 *
 * Global events:
 *
 * | Name               | Arguments               | Triggered                                       |
 * |--------------------|-------------------------|-------------------------------------------------|
 * | HTTP_EVENT.success | response, body, options | After completion, if the response was a success |
 * | HTTP_EVENT.error   | response, body, options | After completion, if the response was an error  |
 *
 * @ignore
 * @param {string|Request} resource - Resource to fetch.
 * @param {string} method - HTTP method (GET, POST...).
 * @param {string|object|Array|FormData|Blob} [body] - Body, e.g. for POST
 *   requests.
 * @param {object} [extraHeaders] - Any extra headers to add.
 * @param {boolean} [returnRaw=false] - Return the full, raw response instead
 *   of just the response body.
 * @returns {Promise.<string|object|Response>} Resolved with the response body
 *   or raw `Response`.
 */
async function _fetch(
  resource,
  method,
  body = null,
  extraHeaders = {},
  returnRaw = false,
) {
  const requestHeaders = {
    'X-REQUESTED-WITH': 'XMLHttpRequest',
    'Ajax-Referrer': globalThis.location?.toString(),
    ...extraHeaders,
  };
  const options = {
    method,
    credentials: 'same-origin',
    headers: requestHeaders,
  };
  if (!isNil(body)) {
    options.body =
      JSON_MIME_TYPE_REGEX.test(requestHeaders['Content-Type']) &&
      (isArray(body) || isPlainObject(body))
        ? JSON.stringify(body)
        : body;
  }

  // Set CSRF token
  const csrfCookie = getCookie('csrftoken');
  if (method !== 'GET' && csrfCookie) {
    options.headers['X-CSRFTOKEN'] = csrfCookie;
  }

  const response = await fetch(resource, options);

  // Can't do a simple equal check since it may also contain `charset` or
  // `boundary` keys.
  const isJson = JSON_MIME_TYPE_REGEX.test(
    response.headers.get('Content-Type'),
  );

  // The body can only be read once. Provide a cloned response for raw requests
  // to make reading the body possible. The clone must be made before the body
  // is read.
  const {
    bodyUsed,
    headers: responseHeaders,
    ok,
    status,
    statusText,
  } = response;
  let responseBody = response.body;
  let clonedResponse;
  if (!bodyUsed) {
    if (returnRaw) {
      clonedResponse = response.clone();
    }
    // response.json() will throw a parse error for empty responses so always
    // read as text and manually parse to JSON to handle it.
    const text = await response.text();
    responseBody = text;
    if (isJson) {
      responseBody = !text || text === 'null' ? null : JSON.parse(text);
    }
  }

  if (!ok) {
    // Trigger global error event
    triggerGlobalCustomEvent(
      HTTP_EVENT.error,
      clonedResponse || response,
      responseBody,
      options,
    );
    throw new RequestError(status, responseBody, statusText);
  }

  // Trigger global success event
  triggerGlobalCustomEvent(
    HTTP_EVENT.success,
    clonedResponse || response,
    responseBody,
    options,
  );

  // Handle redirect
  const location = responseHeaders.get('Ajax-Location');
  if (location) {
    globalThis.stop?.();
    globalThis.location.href = location;
  }

  return returnRaw ? clonedResponse || response : responseBody;
}

/**
 * Wrapped fetch with JSON headers, used for exported helpers.
 *
 * @ignore
 * @param {string|Request} resource - Resource to fetch.
 * @param {string} method - HTTP method (GET, POST...).
 * @param {string|object|Array|FormData|Blob} [body] - Body, e.g. for POST
 *   requests.
 * @param {object} [extraHeaders] - Any extra headers to add.
 * @returns {Promise.<object>} Resolved with the decoded response JSON.
 */
async function _fetchJson(resource, method, body = null, extraHeaders = {}) {
  const isJsonBody = isArray(body) || isPlainObject(body);
  const jsonBody = body && isJsonBody ? JSON.stringify(body) : body || null;
  const headers = {
    Accept: JSON_MIME_TYPE,
    ...extraHeaders,
  };
  // Set even if the body is null/undefined/empty.
  if (isJsonBody || !body) {
    headers['Content-Type'] = JSON_MIME_TYPE;
  }
  return _fetch(resource, method, jsonBody, headers);
}

/**
 * Run a GET request.
 *
 * @param {string|Request} resource - Resource to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<string|object>} Resolved with the response body.
 */
export function httpGet(resource, extraHeaders = {}) {
  return _fetch(resource, 'GET', null, extraHeaders);
}

/**
 * Run a POST request.
 *
 * @param {string|Request} resource - Resource to POST to.
 * @param {string|object|Array|FormData|Blob} [body] - POST body.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<string|object>} Resolved with the response body.
 */
export function httpPost(resource, body = null, extraHeaders = {}) {
  return _fetch(resource, 'POST', body, extraHeaders);
}

/**
 * Run a PUT request.
 *
 * @param {string|Request} resource - Resource to PUT to.
 * @param {string|object|Array|FormData|Blob} [body] - PUT body.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<string|object>} Resolved with the response body.
 */
export function httpPut(resource, body = null, extraHeaders = {}) {
  return _fetch(resource, 'PUT', body, extraHeaders);
}

/**
 * Run a GET request to fetch JSON.
 *
 * Like a regular fetch but with `application/json` Accept and Content-Type
 * headers set. Also parses the response as JSON.
 *
 * @param {string|Request} resource - Resource to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<object>} Resolved with the parsed JSON response.
 */
export function httpGetJSON(resource, extraHeaders = {}) {
  return _fetchJson(resource, 'GET', null, extraHeaders);
}

/**
 * Run a JSON POST request.
 *
 * Like a regular fetch but with `application/json` Accept and Content-Type
 * headers set. Also JSON stringifies the body if applicable and parses the
 * response as JSON.
 *
 * @param {string|Request} resource - Resource to POST to.
 * @param {string|object|Array|FormData|Blob} [body] - POST body.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<object>} Resolved with the parsed JSON response.
 */
export function httpPostJSON(resource, body = null, extraHeaders = {}) {
  return _fetchJson(resource, 'POST', body, extraHeaders);
}

/**
 * Run a JSON PUT request.
 *
 * Like a regular fetch but with `application/json` Accept and Content-Type
 * headers set. Also JSON stringifies the body if applicable and parses the
 * response as JSON.
 *
 * @param {string|Request} resource - Resource to PUT to.
 * @param {string|object|Array|FormData|Blob} [body] - PUT body.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<object>} Resolved with the parsed JSON response.
 */
export function httpPutJSON(resource, body = null, extraHeaders = {}) {
  return _fetchJson(resource, 'PUT', body, extraHeaders);
}

/**
 * Run a raw GET request.
 *
 * @param {string|Request} resource - Resource to get.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<Response>} Resolved with the raw `Response` object.
 */
export function httpGetRaw(resource, extraHeaders = {}) {
  return _fetch(resource, 'GET', null, extraHeaders, true);
}

/**
 * Run a raw POST request.
 *
 * @param {string|Request} resource - Resource to POST to.
 * @param {string|object|Array|FormData|Blob} [body] - POST body.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<Response>} Resolved with the raw `Response` object.
 */
export function httpPostRaw(resource, body = null, extraHeaders = {}) {
  return _fetch(resource, 'POST', body, extraHeaders, true);
}

/**
 * Run a raw PUT request.
 *
 * @param {string|Request} resource - Resource to PUT to.
 * @param {string|object|Array|FormData|Blob} [body] - PUT body.
 * @param {object} [extraHeaders] - Any extra headers to send.
 * @returns {Promise.<Response>} Resolved with the raw `Response` object.
 */
export function httpPutRaw(resource, body = null, extraHeaders = {}) {
  return _fetch(resource, 'PUT', body, extraHeaders, true);
}

/**
 * Load an image.
 *
 * @param {string} src - Image URL.
 * @returns {Promise.<Element>} Resolved with the image object.
 */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(new Error(`Image src '${src}' failed to load`));
    };

    img.src = src;
  });
}

/**
 * Load a JavaScript file.
 *
 * @param {string} src - JavaScript file URL.
 * @returns {Promise.<Element>} Resolved with the script tag object.
 */
export function loadJS(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.onload = () => {
      resolve(script);
    };
    script.onerror = () => {
      reject(new Error(`Script with src '${src}' failed to load`));
    };

    script.src = src;
    document.head.append(script);
  });
}

/**
 * Load a JavaScript file if it's not already on the page.
 *
 * @param {string} src - JavaScript file URL.
 * @returns {Promise.<Element>} Resolved with the existing script tag if any,
 *   or with the newly added script tag.
 */
export function ensureJS(src) {
  // The computed src may be different from the literal, e.g. setting it to
  // '/rel.js' will result in a full 'http://domain.com/rel.js' when read from
  // the src property. Compare against the computed properties to handle
  // different `src` strings referring to the same script.
  const tempScript = document.createElement('script');
  tempScript.src = src;
  const matchingScript = Array.from(document.querySelectorAll('script')).find(
    (pageScript) => pageScript.src === tempScript.src,
  );
  if (matchingScript) {
    return matchingScript;
  }
  return loadJS(src);
}
